<template>
  <v-container fluid>
    <page-title>
      Meilisearch Dashboard
      <template v-slot:buttons>
        <v-btn color="primary"
               @click.prevent="fetchData">
          Refresh
        </v-btn>
      </template>
    </page-title>

    <ErrorAlert v-if="error"
                :error="error">
    </ErrorAlert>

    <v-card class="mb-4">
      <v-card-text>
        <v-radio-group v-model="form.instance"
                       label="Instance"
                       @change="changeInstance"
                       class="mt-0">
          <v-radio v-for="instance in instances"
                   :key="instance.id"
                   :value="instance.id"
                   :label="instance.name">
          </v-radio>
        </v-radio-group>

        <v-autocomplete v-if="indexes.length"
                        v-model="form.index"
                        label="Select an index"
                        :items="indexes"
                        item-value="uid"
                        item-text="name"
                        clearable
                        @change="changeIndex">
        </v-autocomplete>
        <div v-else-if="instance && indexes.length === 0">
          There are no indexes.
        </div>

        <v-text-field v-if="form.index"
                      v-model="form.query"
                      :label="`Search ${form.index}`"
                      append-icon="fal fa-search"
                      @keydown.enter="search"
                      clearable
                      @click:clear="clearSearch">
        </v-text-field>
      </v-card-text>
    </v-card>

    <template v-if="index && results">
      <v-card class="mb-4">
        <v-card-title>
          {{ results.estimatedTotalHits | pluralize('result') }} for {{ results.query }}
        </v-card-title>
        <v-card-text>
          <pre class="indexData">{{ results }}</pre>
        </v-card-text>
      </v-card>
    </template>

    <template v-if="index">
      <v-card class="mb-4">
        <v-card-title>
          Index: {{ index.uid }}
        </v-card-title>
        <v-card-text>
          <pre>{{ index.data }}</pre>
        </v-card-text>
      </v-card>

      <v-row>
        <v-col cols="6">
          <v-card class="mb-4">
            <v-card-title>
              Index Settings
            </v-card-title>
            <v-card-text>
              <pre class="indexData">{{ index.settings }}</pre>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="6">
          <v-card class="mb-4">
            <v-card-title>
              Index Status
            </v-card-title>
            <v-card-text>
              <pre class="indexData">{{ index.stats }}</pre>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-row>
      <v-col v-if="stats"
             cols="6">
        <v-card class="mb-4">
          <v-card-title>
            Instance Stats
          </v-card-title>
          <v-card-text>
            <pre class="systemData">{{ stats }}</pre>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="version"
             cols="6">
        <v-card class="mb-4">
          <v-card-title>
            Instance Version
          </v-card-title>
          <v-card-text>
            <pre class="systemData">{{ version }}</pre>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="experimentalFeatures || metrics">
      <v-col v-if="experimentalFeatures"
             cols="12"
             sm="6">
        <v-card class="mb-4">
          <v-card-title>
            Instance Experimental Features
          </v-card-title>
          <v-card-text>
            <pre class="systemData">{{ experimentalFeatures }}</pre>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col v-if="metrics"
             cols="12"
             sm="6">
        <v-card class="mb-4">
          <v-card-title>
            Instance Metrics
          </v-card-title>
          <v-card-text>
            <pre class="systemData">{{ metrics }}</pre>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="tasks && tasks.length"
            class="mb-4">
      <v-card-title>
        Instance Tasks
        <v-btn right
               icon
               @click.prevent="fetchTasks">
          <v-icon>
            fas fa-sync
          </v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="error"
               @click.prevent="deleteTasks">
          Delete tasks
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-simple-table>
          <thead>
          <tr>
            <th>
              UID
            </th>
            <th>
              Index
            </th>
            <th>
              Status
            </th>
            <th>
              Type
            </th>
            <th>
              Cancelled by
            </th>
            <th>
              Error
            </th>
            <th>
              Enqueued at
            </th>
            <th>
              Started at
            </th>
            <th>
              Finished at
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="task in tasks"
              :key="task.uid">
            <td>
              {{ task.uid }}
            </td>
            <td>
              {{ task.indexUid }}
            </td>
            <td>
              {{ task.status }}
            </td>
            <td>
              {{ task.type }}
            </td>
            <td>
              {{ task.cancelledBy }}
            </td>
            <td>
              {{ task.error }}
            </td>
            <td>
              {{ task.enqueuedAt }}
            </td>
            <td>
              {{ task.startedAt }}
            </td>
            <td>
              {{ task.finishedAt }}
            </td>
          </tr>
          </tbody>
        </v-simple-table>
        <pre v-if="1===2"
             class="systemData">{{ tasks }}</pre>
      </v-card-text>
    </v-card>

  </v-container>
</template>
<style scoped>
pre {
  border: 1px solid rgba(0, 0, 0, 0.12);
  max-height: 300px;
  overflow-y: scroll;
}

.indexData {
  height: 600px;
}

.systemData {
  height: 300px;
}
</style>
<script>
import {find} from 'lodash';
import {Form} from 'vform';
import meilisearchService from '../../services/meilisearchService';
import PageTitle from '../../components/PageTitle.vue';
import ErrorAlert from '../../components/ErrorAlert.vue';

export default {
  components: {ErrorAlert, PageTitle},
  data() {
    return {
      form: new Form({
        instance: '',
        index: '',
        query: '',
      }),
      error: null,
      results: null,
      busy: false,
      index: null,
      instances: [],
      experimentalFeatures: null,
      metrics: null,
    }
  },
  computed: {
    instance() {
      if (this.form.instance) {
        return find(this.instances, instance => instance.id === this.form.instance);
      }
      return null;
    },
    indexes() {
      if (this.instance) {
        const indexes = this.instance.indexes;
        indexes.forEach(index => {
          const statIndex = this.instance.stats.indexes[index.uid];
          index.name = `${index.uid} (${statIndex.numberOfDocuments})`
        });
        return indexes;
      }
      return [];
    },
    stats() {
      if (this.instance) {
        return this.instance.stats;
      }
      return null;
    },
    tasks() {
      if (this.instance && this.instance.tasks) {
        return this.instance.tasks.results;
      }
      return [];
    },
    version() {
      if (this.instance) {
        return this.instance.version;
      }
      return null;
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.error = null;
      this.busy = true;
      //this.instances = [];
      try {
        this.instances = await meilisearchService.getInstances();
        this.instances.forEach(instance => {
          /*instance.indexes = instance.stats.indexes;
          instance.indexes.forEach(index => {
            index.name = `${index.uid} - `
          })*/
        })
      } catch (err) {
        this.error = err;
        this.instances = [];
      } finally {
        this.busy = false;
      }

    },
    async changeInstance() {
      this.form.index = '';
      this.form.query = '';
      this.index = null;
      this.experimentalFeatures = await this.fetchExperimentalFeatures(this.form.instance);
      this.metrics = await this.fetchMetrics(this.form.instance);
    },
    async fetchExperimentalFeatures(instance) {
      try {
        return meilisearchService.getExperimentalFeatures(instance)
      } catch (e) {
        return null;
      }
    },
    async fetchMetrics(instance) {
      try {
        return meilisearchService.getMetrics(instance)
      } catch (e) {
        return null;
      }
    },
    changeIndex() {
      const indexName = this.form.index;
      if (!indexName) {
        this.index = null;
        this.results = null;
        return;
      }
      this.index = {
        name: indexName,
        data: null,
        settings: null,
        stats: null,
      };
      meilisearchService.getIndex(indexName)
        .then(data => {
          this.index.data = data;
        });

      meilisearchService.getIndexSettings(indexName)
        .then(data => {
          this.index.settings = data;
        });

      meilisearchService.getIndexStats(indexName)
        .then(data => {
          this.index.stats = data;
        })

      this.search();
    },
    fetchTasks() {
      if (this.instance) {
        meilisearchService.getTasks(this.instance.id)
          .then(data => {
            const instance = find(this.instances, i => i.id === this.instance.id);
            if (instance) {
              instance.tasks = data;
            }
          })
          .catch(err => {
            const instance = find(this.instances, i => i.id === this.instance.id);
            if (instance) {
              instance.tasks = null;
            }
          })
      }
    },
    clearSearch() {
      this.results = null;
    },
    search() {
      if (!this.form.index || !this.form.query) {
        this.results = null;
        return;
      }
      meilisearchService.search(this.form.index, this.form.query)
        .then(data => {
          this.results = data;
        })
        .catch(err => {
          this.results = null;
        })
    },
    deleteTasks() { // MLD-5132
      const form = {
        uids: '*',
      };
      return meilisearchService.deleteTasks(this.form.instance, form)
        .then(data => {
          this.fetchTasks();
        })
        .catch(err => {
          this.error = err;
        })
    }
  }
}
</script>
